import React from 'react';
import { Gift, Check, Calendar } from 'lucide-react';
import { ScrollFadeIn } from './ScrollFadeIn';
export function BonusStack() {
  return <section className="py-8 bg-slate-900 space-y-10" data-id="element-34">
      {/* BONUS 1 — The 3AM Rescue Kit */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-35">
        <ScrollFadeIn data-id="element-36">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden" data-id="element-37">
            <div className="grid md:grid-cols-2 gap-0" data-id="element-38">
              {/* Image */}
              <div className="bg-slate-900 flex items-center justify-center p-8 md:p-12" data-id="element-39">
                <img src="https://cdn.magicpatterns.com/uploads/rcZcghPkokYckJZzRYhPcJ/3AM-Rescue-Kit.png" alt="The 3AM Rescue Kit bonus guide cover" className="w-full max-w-sm rounded-lg shadow-2xl" data-id="element-40" />
              </div>

              {/* Content */}
              <div className="p-8 md:p-10 space-y-6" data-id="element-41">
                <div data-id="element-42">
                  <div className="inline-flex items-center gap-2 text-amber-500 text-sm font-semibold uppercase tracking-widest mb-3" data-id="element-43">
                    <Gift className="w-4 h-4" data-id="element-44" />
                    <span data-id="element-45">Bonus #1</span>
                  </div>
                  <h3 className="text-3xl font-serif text-slate-50 mb-2" data-id="element-46">
                    The 3AM Rescue Kit
                  </h3>
                  <p className="text-slate-400 text-sm mb-1" data-id="element-47">
                    Fast-Back-to-Sleep Protocol
                  </p>
                  <p className="text-sm" data-id="element-48">
                    <span className="text-slate-500" data-id="element-49">Value: $37 — </span>
                    <span className="text-emerald-400 font-semibold" data-id="element-50">
                      Yours FREE today
                    </span>
                  </p>
                </div>

                <div className="space-y-4 text-slate-300 leading-relaxed text-sm" data-id="element-51">
                  <p data-id="element-52">
                    You're doing everything right — eating better, following the
                    protocol, winding down earlier.
                  </p>
                  <p data-id="element-53">
                    Then 3:14 AM hits. Your heart is thumping. Your mind is
                    looping. And you're lying in the dark wondering if something
                    is wrong with you.
                  </p>
                  <p data-id="element-54">
                    Nothing is wrong with you. Your body just fired a chemical
                    alarm — and without a plan, you'll spend the next 90 minutes
                    staring at the ceiling.
                  </p>
                  <p className="text-slate-50 font-medium" data-id="element-55">
                    The 3AM Rescue Kit is a decision-based rapid-response system
                    for exactly that moment.
                  </p>
                  <p data-id="element-56">
                    Instead of guessing what to do half-asleep, you follow a
                    simple 3-route decision tree:
                  </p>
                </div>

                {/* Routes */}
                <div className="space-y-3" data-id="element-57">
                  <div className="flex items-start gap-3" data-id="element-58">
                    <div className="w-5 h-5 rounded-full bg-blue-400 shrink-0 mt-0.5" data-id="element-59" />
                    <p className="text-slate-300 text-sm" data-id="element-60">
                      <span className="text-slate-50 font-medium" data-id="element-61">
                        Route A:
                      </span>{' '}
                      Heart racing (body on high alert) — 3-step physical reset
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-62">
                    <div className="w-5 h-5 rounded-full bg-amber-400 shrink-0 mt-0.5" data-id="element-63" />
                    <p className="text-slate-300 text-sm" data-id="element-64">
                      <span className="text-slate-50 font-medium" data-id="element-65">
                        Route B:
                      </span>{' '}
                      Real physical hunger — rescue snack rules that won't spike
                      cortisol
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-66">
                    <div className="w-5 h-5 rounded-full bg-emerald-400 shrink-0 mt-0.5" data-id="element-67" />
                    <p className="text-slate-300 text-sm" data-id="element-68">
                      <span className="text-slate-50 font-medium" data-id="element-69">
                        Route C:
                      </span>{' '}
                      Mind looping — a 4-line journal script that stops
                      rumination cold
                    </p>
                  </div>
                </div>

                {/* Checklist */}
                <div className="space-y-2.5 pt-2" data-id="element-70">
                  <p className="text-slate-400 text-xs uppercase tracking-widest font-semibold mb-3" data-id="element-71">
                    Inside the kit:
                  </p>
                  <div className="flex items-start gap-2.5" data-id="element-72">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-73" />
                    <span className="text-slate-300 text-sm" data-id="element-74">
                      The 60-second decision tree (usable half-asleep, no
                      thinking required)
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-75">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-76" />
                    <span className="text-slate-300 text-sm" data-id="element-77">
                      The 4-7-8 Vagal Brake sequence for heart-racing wake-ups
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-78">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-79" />
                    <span className="text-slate-300 text-sm" data-id="element-80">
                      Rescue snack rules that fuel your liver without triggering
                      a new cortisol spike
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-81">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-82" />
                    <span className="text-slate-300 text-sm" data-id="element-83">
                      A 4-line anti-rumination script (read it out loud, lights
                      off)
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-84">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-85" />
                    <span className="text-slate-300 text-sm" data-id="element-86">
                      The 7 mistakes that keep you awake — and what to do
                      instead
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-87">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-88" />
                    <span className="text-slate-300 text-sm" data-id="element-89">
                      A printable one-pager to keep on your nightstand
                    </span>
                  </div>
                </div>

                <p className="text-slate-50 font-medium text-sm" data-id="element-90">
                  This kit does one thing: gets you back to sleep faster —
                  starting tonight.
                </p>

                <div className="border-l-2 border-amber-500 pl-4 bg-amber-500/5 py-3" data-id="element-91">
                  <p className="text-amber-400 italic text-sm leading-relaxed" data-id="element-92">
                    "Most people lie awake because they don't have a plan. This
                    kit is the plan."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ScrollFadeIn>
      </div>

      {/* BONUS 2 — Cortisol-Safe Grocery Map */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-93">
        <ScrollFadeIn data-id="element-94">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden" data-id="element-95">
            <div className="grid md:grid-cols-2 gap-0" data-id="element-96">
              {/* Content (left on desktop for alternating) */}
              <div className="p-8 md:p-10 space-y-6 order-2 md:order-1" data-id="element-97">
                <div data-id="element-98">
                  <div className="inline-flex items-center gap-2 text-amber-500 text-sm font-semibold uppercase tracking-widest mb-3" data-id="element-99">
                    <Gift className="w-4 h-4" data-id="element-100" />
                    <span data-id="element-101">Bonus #2</span>
                  </div>
                  <h3 className="text-3xl font-serif text-slate-50 mb-2" data-id="element-102">
                    Cortisol-Safe Grocery Map
                  </h3>
                  <p className="text-slate-400 text-sm mb-1" data-id="element-103">
                    Done-for-You Shopping List + Night Staples (14 Days)
                  </p>
                  <p className="text-sm" data-id="element-104">
                    <span className="text-slate-500" data-id="element-105">Value: $37 — </span>
                    <span className="text-emerald-400 font-semibold" data-id="element-106">
                      Yours FREE today
                    </span>
                  </p>
                </div>

                <div className="space-y-4 text-slate-300 leading-relaxed text-sm" data-id="element-107">
                  <p data-id="element-108">
                    The protocol works. But it only works if you have the right
                    food in the house.
                  </p>
                  <p data-id="element-109">
                    Most people start strong — then hit Wednesday with an empty
                    fridge and grab whatever is easiest. That "whatever" is
                    usually what triggers the 3AM spike in the first place.
                  </p>
                  <p className="text-slate-50 font-medium" data-id="element-110">
                    The Cortisol-Safe Grocery Map removes that problem entirely.
                  </p>
                  <p data-id="element-111">
                    It's not a meal plan. It's a shopping system — designed so
                    that one trip to the store sets you up for two full weeks of
                    calmer evenings and steadier sleep.
                  </p>
                </div>

                {/* Checklist */}
                <div className="space-y-2.5 pt-2" data-id="element-112">
                  <p className="text-slate-400 text-xs uppercase tracking-widest font-semibold mb-3" data-id="element-113">
                    Inside the map:
                  </p>
                  <div className="flex items-start gap-2.5" data-id="element-114">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-115" />
                    <span className="text-slate-300 text-sm" data-id="element-116">
                      The 12 Night Staples — the core items that stabilize
                      nighttime blood sugar
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-117">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-118" />
                    <span className="text-slate-300 text-sm" data-id="element-119">
                      An aisle-by-aisle shopping list (produce, protein, pantry,
                      frozen)
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-120">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-121" />
                    <span className="text-slate-300 text-sm" data-id="element-122">
                      The Cortisol-Safe Dinner Formula (protein + fiber + slow
                      carb + fat)
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-123">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-124" />
                    <span className="text-slate-300 text-sm" data-id="element-125">
                      14 simple dinner + snack combos — ready in 10-15 minutes,
                      no recipes needed
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-126">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-127" />
                    <span className="text-slate-300 text-sm" data-id="element-128">
                      Easy substitutions for dairy-free, gluten-free, and
                      vegetarian needs
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-129">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-130" />
                    <span className="text-slate-300 text-sm" data-id="element-131">
                      A "Better to Avoid" list — the 5 evening trigger foods
                      most people don't suspect
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-132">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-133" />
                    <span className="text-slate-300 text-sm" data-id="element-134">
                      A fill-in "Your First Cart" template to take to the store
                      today
                    </span>
                  </div>
                </div>

                <p className="text-slate-50 font-medium text-sm" data-id="element-135">
                  You don't need willpower. You need a system. This map is the
                  system.
                </p>

                <div className="border-l-2 border-amber-500 pl-4 bg-amber-500/5 py-3" data-id="element-136">
                  <p className="text-amber-400 italic text-sm leading-relaxed" data-id="element-137">
                    "If it's in the house, you'll eat it. If it's not, you won't
                    reach for it. This list makes the right choice the easy
                    choice."
                  </p>
                </div>
              </div>

              {/* Image (right on desktop) */}
              <div className="bg-slate-900 flex items-center justify-center p-8 md:p-12 order-1 md:order-2" data-id="element-138">
                <img src="https://cdn.magicpatterns.com/uploads/aDH1GgafDem32o1ZEft9G8/Cortisol-Safe-Grocery-Map.png" alt="Cortisol-Safe Grocery Map bonus guide cover" className="w-full max-w-sm rounded-lg shadow-2xl" data-id="element-139" />
              </div>
            </div>
          </div>
        </ScrollFadeIn>
      </div>

      {/* BONUS 3 — The Coffee Exit Plan */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-140">
        <ScrollFadeIn data-id="element-141">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden" data-id="element-142">
            <div className="grid md:grid-cols-2 gap-0" data-id="element-143">
              {/* Image */}
              <div className="bg-slate-900 flex items-center justify-center p-8 md:p-12" data-id="element-144">
                <img src="https://cdn.magicpatterns.com/uploads/cuSNPLuFnNdPTDBYMEMsKM/Coffee-Exit-Plan.png" alt="The Coffee Exit Plan bonus guide cover" className="w-full max-w-sm rounded-lg shadow-2xl" data-id="element-145" />
              </div>

              {/* Content */}
              <div className="p-8 md:p-10 space-y-6" data-id="element-146">
                <div data-id="element-147">
                  <div className="inline-flex items-center gap-2 text-amber-500 text-sm font-semibold uppercase tracking-widest mb-3" data-id="element-148">
                    <Gift className="w-4 h-4" data-id="element-149" />
                    <span data-id="element-150">Bonus #3</span>
                  </div>
                  <h3 className="text-3xl font-serif text-slate-50 mb-2" data-id="element-151">
                    The Coffee Exit Plan
                  </h3>
                  <p className="text-slate-400 text-sm mb-1" data-id="element-152">
                    2-Week Caffeine Taper Without the Crash
                  </p>
                  <p className="text-sm" data-id="element-153">
                    <span className="text-slate-500" data-id="element-154">Value: $37 — </span>
                    <span className="text-emerald-400 font-semibold" data-id="element-155">
                      Yours FREE today
                    </span>
                  </p>
                </div>

                <div className="space-y-4 text-slate-300 leading-relaxed text-sm" data-id="element-156">
                  <p data-id="element-157">
                    Here's the part nobody tells you about cortisol and coffee:
                  </p>
                  <p data-id="element-158">
                    If you drink your first cup within 30 minutes of waking — on
                    an empty stomach — you are pouring gasoline on a cortisol
                    fire.
                  </p>
                  <p data-id="element-159">
                    Your body already produces a natural cortisol peak in the
                    first 30-45 minutes after waking. That's your built-in
                    "starter motor." When you add caffeine on top of that peak,
                    you:
                  </p>
                  <ul className="space-y-1.5 pl-1 text-slate-300" data-id="element-160">
                    <li data-id="element-161">Spike cortisol even higher than necessary</li>
                    <li data-id="element-162">Guarantee a hard crash by early afternoon</li>
                    <li data-id="element-163">Reach for more caffeine to compensate</li>
                    <li data-id="element-164">
                      Disrupt your sleep — which means you need even more coffee
                      tomorrow
                    </li>
                  </ul>
                  <p data-id="element-165">
                    It's a loop. And The Coffee Exit Plan breaks it — without
                    asking you to quit cold turkey or "push through" the
                    headaches.
                  </p>
                </div>

                {/* Phases */}
                <div className="space-y-3" data-id="element-166">
                  <p className="text-slate-400 text-xs uppercase tracking-widest font-semibold" data-id="element-167">
                    The plan runs for 14 days in 3 simple phases:
                  </p>
                  <div className="flex items-start gap-3" data-id="element-168">
                    <Calendar className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" data-id="element-169" />
                    <p className="text-slate-300 text-sm" data-id="element-170">
                      <span className="text-slate-50 font-medium" data-id="element-171">
                        Days 1-3:
                      </span>{' '}
                      Timing reset (no reduction yet — just shift when you drink
                      it)
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-172">
                    <Calendar className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" data-id="element-173" />
                    <p className="text-slate-300 text-sm" data-id="element-174">
                      <span className="text-slate-50 font-medium" data-id="element-175">
                        Days 4-7:
                      </span>{' '}
                      Gentle taper (reduce by one small step using your chosen
                      method)
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-176">
                    <Calendar className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" data-id="element-177" />
                    <p className="text-slate-300 text-sm" data-id="element-178">
                      <span className="text-slate-50 font-medium" data-id="element-179">
                        Days 8-14:
                      </span>{' '}
                      Stabilize (lock in your new baseline — steady energy all
                      day)
                    </p>
                  </div>
                </div>

                {/* Checklist */}
                <div className="space-y-2.5 pt-2" data-id="element-180">
                  <p className="text-slate-400 text-xs uppercase tracking-widest font-semibold mb-3" data-id="element-181">
                    Inside the plan:
                  </p>
                  <div className="flex items-start gap-2.5" data-id="element-182">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-183" />
                    <span className="text-slate-300 text-sm" data-id="element-184">
                      The 4 Timing Rules that reduce jitters and crashes
                      immediately
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-185">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-186" />
                    <span className="text-slate-300 text-sm" data-id="element-187">
                      A 2-minute baseline setup so you know exactly where to
                      start
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-188">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-189" />
                    <span className="text-slate-300 text-sm" data-id="element-190">
                      The cut-off time calculator based on your bedtime
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-191">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-192" />
                    <span className="text-slate-300 text-sm" data-id="element-193">
                      10 non-caffeine energy boosters (no supplements — just
                      physiology)
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-194">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-195" />
                    <span className="text-slate-300 text-sm" data-id="element-196">
                      The 2PM Crash Protocol (5 minutes to stop the afternoon
                      slump)
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-197">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-198" />
                    <span className="text-slate-300 text-sm" data-id="element-199">
                      Troubleshooting for headaches and irritability (fix
                      without quitting)
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5" data-id="element-200">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-201" />
                    <span className="text-slate-300 text-sm" data-id="element-202">
                      A 14-day daily tracker (1 minute per day)
                    </span>
                  </div>
                </div>

                <p className="text-slate-50 font-medium text-sm" data-id="element-203">
                  You don't have to give up coffee. You just have to stop
                  letting coffee run your nervous system.
                </p>

                <div className="border-l-2 border-amber-500 pl-4 bg-amber-500/5 py-3" data-id="element-204">
                  <p className="text-amber-400 italic text-sm leading-relaxed" data-id="element-205">
                    "The goal isn't zero caffeine. The goal is caffeine that
                    works for you — not against you."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ScrollFadeIn>
      </div>
    </section>;
}